import { User, PlanType } from '../types';
import apiRequest, { clearAuthData } from './api';
import { errorTracker } from './errorTracker';

export const authService = {
  // Register with backend API (cookie-based)
  register: async (name: string, email: string, password: string, whatsappNumber?: string): Promise<User> => {
    const response = await apiRequest<{ user: User; message?: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, whatsappNumber }),
    });

    // Cookies are set automatically by server - no token management needed
    localStorage.setItem('orca_user', JSON.stringify(response.user));
    
    // Log registration message if present
    if (response.message) {
      console.log('Registration:', response.message);
    }
    
    return response.user;
  },

  // Login with backend API (cookie-based)
  login: async (email: string, password: string, twoFactorToken?: string): Promise<User> => {
    try {
      const response = await apiRequest<{ user: User; requires2FA?: boolean; userId?: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, twoFactorToken }),
        timeout: 60000, // 60 seconds timeout for login to prevent premature timeouts
      });

      // Check if 2FA is required
      if (response.requires2FA) {
        // Return special response indicating 2FA is needed
        throw new Error('2FA_REQUIRED');
      }

      // Cookies are set automatically by server - no token management needed
      localStorage.setItem('orca_user', JSON.stringify(response.user));
      
      errorTracker.trackInfo('Login successful', {
        source: 'api',
        component: 'authService',
        action: 'login-success',
        context: { email }
      });
      
      return response.user;
    } catch (error: any) {
      if (error.message === '2FA_REQUIRED') {
        // Re-throw as special error for 2FA handling
        throw error;
      }
      errorTracker.trackError(error, {
        source: 'api',
        component: 'authService',
        action: 'login-failed',
        context: { email, errorMessage: error.message }
      });
      throw error;
    }
  },

  // Logout - calls API to clear cookies
  logout: async (): Promise<void> => {
    try {
      await apiRequest('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error during logout:', error);
      // Clear local data even if API call fails
    } finally {
      clearAuthData();
    }
  },

  getCurrentUser: async (): Promise<User | null> => {
    try {
      const user = await apiRequest<User>('/auth/me');
      localStorage.setItem('orca_user', JSON.stringify(user));
      return user;
    } catch (error: any) {
      // If token is invalid or no token provided, clear storage and return null
      // This is expected behavior when user is not logged in - don't log as error
      const isExpectedError = error.message?.includes('No token provided') || 
                              error.message?.includes('Session expired') ||
                              error.message?.includes('Invalid token');
      
      if (!isExpectedError) {
        // Only track unexpected errors
        errorTracker.trackError(error, {
          source: 'api',
          component: 'authService',
          action: 'getCurrentUser-failed',
          context: { errorMessage: error.message }
        });
      }
      
      clearAuthData();
      return null;
    }
  },

  // Get cached user (for initial load)
  getCachedUser: (): User | null => {
    const cached = localStorage.getItem('orca_user');
    return cached ? JSON.parse(cached) : null;
  },

  // CREDIT SYSTEM - Deduct credit via API
  deductCredit: async (): Promise<{ credits: number; isPremium: boolean }> => {
    return apiRequest<{ credits: number; isPremium: boolean }>('/credits/deduct', {
      method: 'POST',
      timeout: 15000, // 15 seconds timeout for credit checks
    });
  },

  // Get credit balance
  getCreditBalance: async (): Promise<{ credits: number; plan: string; isPremium: boolean; subscriptionExpiry: string | null }> => {
    return apiRequest('/credits/balance', {
      timeout: 15000, // 15 seconds timeout for credit checks
    });
  },

  // Verify email with code
  verifyEmail: async (code: string, email?: string): Promise<User> => {
    const response = await apiRequest<{ user: User; message: string }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ code, email }),
    });
    localStorage.setItem('orca_user', JSON.stringify(response.user));
    return response.user;
  },

  // Resend verification code
  resendVerification: async (): Promise<void> => {
    await apiRequest('/auth/resend-verification', {
      method: 'POST',
    });
  },

  // Create PayPal order
  createPayPalOrder: async (plan: PlanType): Promise<{ orderId: string; approvalUrl: string; plan: string }> => {
    return apiRequest<{ orderId: string; approvalUrl: string; plan: string }>('/payments/create-order', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    });
  },

  // Capture PayPal payment
  capturePayPalOrder: async (orderId: string, plan: PlanType): Promise<{ success: boolean; user: User }> => {
    return apiRequest<{ success: boolean; user: User }>('/payments/capture-order', {
      method: 'POST',
      body: JSON.stringify({ orderId, plan }),
    });
  },

  // ADMIN: GET ALL USERS
  getAllUsers: async (filters?: {
    search?: string;
    plan?: string;
    emailVerified?: string;
    lowCredits?: string;
    usedCredits?: string;
    paidPlans?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    skip?: number;
  }): Promise<{ users: User[]; total: number; limit: number; skip: number }> => {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    return apiRequest<{ users: User[]; total: number; limit: number; skip: number }>(`/users${queryString ? `?${queryString}` : ''}`);
  },

  // ADMIN: Update user credits
  updateUserCredits: async (userId: string, credits: number): Promise<User> => {
    return apiRequest<User>(`/users/${userId}/credits`, {
      method: 'PATCH',
      body: JSON.stringify({ credits }),
    });
  },

  // ADMIN: Create user
  createUser: async (userData: { name: string; email: string; password: string; role?: string; credits?: number; plan?: string; isApproved?: boolean }): Promise<User> => {
    return apiRequest<User>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  // ADMIN: Approve/Disapprove user
  approveUser: async (userId: string, isApproved: boolean): Promise<User> => {
    return apiRequest<User>(`/users/${userId}/approve`, {
      method: 'PATCH',
      body: JSON.stringify({ isApproved }),
    });
  },

  // ADMIN: Update user
  updateUser: async (userId: string, userData: Partial<User>): Promise<User> => {
    return apiRequest<User>(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(userData),
    });
  },

  // ADMIN: Delete user
  deleteUser: async (userId: string): Promise<void> => {
    return apiRequest(`/users/${userId}`, {
      method: 'DELETE',
    });
  },

  // ADMIN: Kick user
  kickUser: async (userId: string, reason?: string): Promise<User> => {
    return apiRequest<User>(`/users/${userId}/kick`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  // ADMIN: Restore user access
  restoreUser: async (userId: string): Promise<User> => {
    return apiRequest<User>(`/users/${userId}/restore`, {
      method: 'POST',
    });
  },

  // ADMIN: Get user activity
  getUserActivity: async (userId: string): Promise<any[]> => {
    return apiRequest<any[]>(`/users/${userId}/activity`);
  },

  // ADMIN: Get user consumption
  getUserConsumption: async (userId: string): Promise<any> => {
    return apiRequest<any>(`/users/${userId}/consumption`);
  },

  // USER: Get own activity/history
  getMyActivity: async (): Promise<any[]> => {
    return apiRequest<any[]>('/users/me/activity');
  },

  // USER: Get own consumption
  getMyConsumption: async (): Promise<any> => {
    return apiRequest<any>('/users/me/consumption');
  },

  // Generate PDF Report
  generatePDFReport: async (report: any): Promise<Blob> => {
    // Use the same API_URL logic as api.ts to ensure consistency
    const API_URL = import.meta.env?.VITE_API_URL 
      ? (import.meta.env.VITE_API_URL.endsWith('/api') 
          ? import.meta.env.VITE_API_URL 
          : `${import.meta.env.VITE_API_URL}/api`)
      : (typeof window !== 'undefined' && window.location.origin.includes('localhost') 
          ? 'http://localhost:3001/api' 
          : '/api');
    const fullUrl = `${API_URL}/analysis/generate-pdf`;
    
    console.log('[PDF] Generating PDF report...', { 
      apiUrl: fullUrl,
      usingCookies: true,
      reportKeys: Object.keys(report || {}),
      reportScore: report?.overallScore
    });
    
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        credentials: 'include' as RequestCredentials, // Include cookies
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ report })
      });

      console.log('[PDF] Response received:', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        let error;
        try {
          error = JSON.parse(errorText);
        } catch {
          error = { error: errorText || 'Failed to generate PDF' };
        }
        
        console.error('[PDF] Generation failed:', {
          status: response.status,
          statusText: response.statusText,
          error: error,
          url: fullUrl
        });
        
        // Provide more specific error messages
        if (response.status === 404) {
          throw new Error(`PDF route not found. Please ensure the backend server is running and the route is registered. URL: ${fullUrl}`);
        }
        
        throw new Error(error.error || error.message || `Failed to generate PDF: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('[PDF] PDF blob received:', {
        size: blob.size,
        type: blob.type
      });
      
      return blob;
    } catch (fetchError: any) {
      console.error('[PDF] Fetch error:', fetchError);
      
      // Network errors
      if (fetchError.message?.includes('Failed to fetch') || fetchError.message?.includes('NetworkError')) {
        throw new Error('Network error: Could not connect to the server. Please check if the backend server is running.');
      }
      
      throw fetchError;
    }
  },

  // ADMIN: Get payment history
  getPaymentHistory: async (filters?: {
    userId?: string;
    plan?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    limit?: number;
    skip?: number;
  }): Promise<{ payments: any[]; total: number; limit: number; skip: number }> => {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    return apiRequest<{ payments: any[]; total: number; limit: number; skip: number }>(`/payments/history${queryString ? `?${queryString}` : ''}`);
  },

  // Update user preferences
  updatePreferences: async (preferredLanguage: string, uiLanguage: string, aiAssistantLanguage: string, preferredCountry: string): Promise<User> => {
    const user = await apiRequest<User>('/auth/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ preferredLanguage, uiLanguage, aiAssistantLanguage, preferredCountry }),
    });
    // Update cached user
    localStorage.setItem('orca_user', JSON.stringify(user));
    return user;
  },

  // Refresh access token (if expired)
  refreshToken: async (): Promise<boolean> => {
    try {
      await apiRequest<{ success: boolean }>('/auth/refresh', {
        method: 'POST',
      });
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  },

  // Verify PayPal payment by transaction ID (for payment links)
  verifyPayPalPayment: async (transactionId: string): Promise<User> => {
    const response = await apiRequest<{ user: User; message: string; success: boolean }>('/payments/verify-payment', {
      method: 'POST',
      body: JSON.stringify({ transactionId }),
    });
    localStorage.setItem('orca_user', JSON.stringify(response.user));
    return response.user;
  },

  // Get credit usage history and statistics
  getCreditUsage: async (): Promise<{
    usageHistory: any[];
    usageStats: Array<{
      action: string;
      toolName: string;
      usageCount: number;
      totalCredits: number;
      costPerUse: number;
    }>;
    totals: {
      totalCreditsUsed: number;
      totalUsageCount: number;
      averageCreditsPerUse: string;
    };
    toolCosts: Array<{
      action: string;
      toolName: string;
      cost: number;
    }>;
  }> => {
    const response = await apiRequest<any>('/credits/usage');
    // Normalize response to ensure all properties are arrays/objects
    return {
      usageHistory: Array.isArray(response?.usageHistory) ? response.usageHistory : [],
      usageStats: Array.isArray(response?.usageStats) ? response.usageStats : [],
      totals: {
        totalCreditsUsed: typeof response?.totals?.totalCreditsUsed === 'number' ? response.totals.totalCreditsUsed : 0,
        totalUsageCount: typeof response?.totals?.totalUsageCount === 'number' ? response.totals.totalUsageCount : 0,
        averageCreditsPerUse: typeof response?.totals?.averageCreditsPerUse === 'string' ? response.totals.averageCreditsPerUse : '0'
      },
      toolCosts: Array.isArray(response?.toolCosts) ? response.toolCosts : []
    };
  },

  // Test PayPal connection
  testPayPalConnection: async (): Promise<{ success: boolean; message: string; error?: string }> => {
    return apiRequest<{ success: boolean; message: string; error?: string }>('/payments/test-connection');
  },
};